// SPDX-License-Identifier: AGPL-3.0-only
import { request } from './client';

/**
 * The managed-device API.
 *
 * # WHAT IS NOT IN THIS FILE
 *
 * Any function that sends a command. The write is `setPortState`, which carries
 * a port and a boolean; the commands that implement it live in the driver
 * definition on the server and are chosen by neither this code nor its caller.
 *
 * If a function ever appears here taking a command string, every boundary
 * beneath it has been undone.
 *
 * # THE THREE OUTCOMES ARE THE PRODUCT
 *
 * `SUCCESS`, `FAILED` and `UNKNOWN` are not error handling. They are three
 * different facts about a change that was attempted, and the UI must show them
 * as three different things — an UNKNOWN that looks like a success is the worst
 * outcome this product can produce.
 */

/** What Meshvara established about an operation it performed. */
export type Outcome = 'SUCCESS' | 'FAILED' | 'UNKNOWN';

/**
 * Where a device's SSH identity stands.
 *
 * `changed` is the loud one: a key was confirmed and the device presented a
 * different one. The network is working; the machine answering is not provably
 * the one an operator trusted.
 */
export type TrustState = 'untrusted' | 'pending' | 'trusted' | 'changed';

export interface Device {
  id: string;
  company_id: string;
  site_id: string;
  gateway_id: string;
  name: string;
  driver_id: string;
  management_address: string;
  management_port: number;
  management_username: string;
  /** For diagnostics only. Ordinary actions are protocol-neutral. */
  transport: string;
  credential_set: boolean;
  ssh_trust_state: TrustState;
  ssh_trusted_fingerprint?: string;
  ssh_trusted_algorithm?: string;
  ssh_pending_fingerprint?: string;
  ssh_pending_algorithm?: string;
  /**
   * The exact key the Gateway observed, and the approval token.
   *
   * A client that trusts this device sends this value back; the server accepts
   * only if it still matches. So the approval is bound to the key the operator
   * was actually shown, and a device presenting a different one between the
   * check and the click is refused — without anyone retyping a digest.
   */
  ssh_pending_host_key?: string;
  last_seen_at?: string;
  created_at: string;
}

/**
 * One port.
 *
 * EVERY FIELD MAY BE UNKNOWN, and `unknown` is a value rather than a gap. A
 * switch that did not report a speed is a switch that did not say so — the UI
 * shows a dash, never a zero, and never a plausible default.
 */
export interface Port {
  index: number;
  name: string;
  description?: string;
  admin_state: 'enabled' | 'disabled' | 'testing' | 'unknown';
  link_state: 'up' | 'down' | 'unknown';
  speed_bps?: number;
  mac_address?: string;
}

export interface PortsResult {
  outcome: Outcome;
  detail?: string;
  ssh_state?: string;
  ports: Port[];
}

export interface PortStateResult extends PortsResult {
  interface_id: string;
  enabled: boolean;
}

export interface ProbeResult {
  ssh_state: string;
  algorithm: string;
  fingerprint: string;
  device: Device;
}

/** An approved driver, and what it can actually do. */
export interface Driver {
  driver_id: string;
  name: string;
  manufacturer: string;
  driver_version: string;
  supported_models: string[];
  transport: string;
  default_port: number;
  /**
   * The operations this driver implements.
   *
   * THE UI OFFERS A CONTROL ONLY WHEN THIS LIST CONTAINS THE OPERATION BEHIND
   * IT. Meshvara knowing an operation exists is not the same as this device
   * being able to perform it, and a button that fails when clicked is worse than
   * one that is not there.
   */
  operations: string[];
}

interface List<T> {
  items: T[];
}

export interface NewDevice {
  company_id: string;
  site_id: string;
  gateway_id: string;
  name: string;
  driver_id: string;
  management_address: string;
  management_port: number;
  management_username: string;
  /** Write-only. No response carries it back. */
  management_password: string;
}

export const devicesApi = {
  drivers: () => request<List<Driver>>('/api/v1/drivers').then((r) => r.items),

  list: () => request<List<Device>>('/api/v1/devices').then((r) => r.items),

  get: (id: string) => request<Device>(`/api/v1/devices/${id}`),

  create: (body: NewDevice) => request<Device>('/api/v1/devices', { method: 'POST', body }),

  /**
   * Learn the device's SSH host key. NO PASSWORD IS SENT.
   *
   * What runs before anyone has decided anything. The presented key comes back
   * for a human to compare and is recorded as pending, which authorises nothing.
   */
  probe: (id: string) => request<ProbeResult>(`/api/v1/devices/${id}/ssh-probe`, { method: 'POST' }),

  /**
   * Confirm the pending host key.
   *
   * The key is echoed back, not typed: it must still match what the device
   * presented, so the approval applies to that exact key and not to whatever
   * happens to be pending when the button is pressed.
   */
  trust: (id: string, pendingHostKey: string) =>
    request<Device>(`/api/v1/devices/${id}/ssh-trust`, {
      method: 'POST',
      body: { pending_host_key: pendingHostKey },
    }),

  /** Replace a key that CHANGED. A separate, audited action. */
  replaceTrust: (id: string, pendingHostKey: string) =>
    request<Device>(`/api/v1/devices/${id}/ssh-trust/replace`, {
      method: 'POST',
      body: { pending_host_key: pendingHostKey },
    }),

  ports: (id: string) => request<PortsResult>(`/api/v1/devices/${id}/ports`),

  /**
   * Enable or disable one port, verified by a read-back.
   *
   * Returns the outcome rather than throwing on a failed change: FAILED and
   * UNKNOWN are answers to a question that was asked and performed, and a caller
   * must be able to tell them apart. Only a transport or authorization failure
   * raises.
   */
  setPortState: (id: string, port: number, enabled: boolean) =>
    request<PortStateResult>(`/api/v1/devices/${id}/port-state`, {
      method: 'POST',
      body: { port, enabled },
    }),
};

/** Render a speed the device actually reported, or a dash. */
export function formatSpeed(bps?: number): string {
  if (!bps) return '—';
  if (bps >= 1_000_000_000) {
    const g = bps / 1_000_000_000;
    return `${Number.isInteger(g) ? g : g.toFixed(1)} Gbps`;
  }
  return `${Math.round(bps / 1_000_000)} Mbps`;
}
